import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { UpdateUserPayload, UserDetail, UserStatus } from "../types";

interface UserFormProps {
  user: UserDetail;
  onSubmit: (payload: UpdateUserPayload) => void;
  isSubmitting?: boolean;
  readOnly?: boolean;
  onCancel?: () => void;
}

export function UserForm({
  user,
  onSubmit,
  isSubmitting,
  readOnly = false,
  onCancel,
}: UserFormProps) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [status, setStatus] = useState<UserStatus>(user.status);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl ?? "");

  // Reset local form state when the underlying user changes or when leaving
  // edit mode, so a cancelled edit doesn't leak stale input into the next
  // edit session.
  useEffect(() => {
    setName(user.name);
    setEmail(user.email);
    setStatus(user.status);
    setAvatarUrl(user.avatarUrl ?? "");
  }, [user, readOnly]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    onSubmit({
      name,
      email,
      status,
      avatarUrl: avatarUrl.trim() ? avatarUrl.trim() : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-2">
        <Label htmlFor="name">Full name</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={readOnly}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="email">Email address</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={readOnly}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="status">Status</Label>
        <Select
          id="status"
          value={status}
          onChange={(e) => setStatus(e.target.value as UserStatus)}
          disabled={readOnly}
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="avatarUrl">Avatar URL</Label>
        <Input
          id="avatarUrl"
          type="url"
          placeholder="https://…"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          disabled={readOnly}
        />
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-2 pt-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </form>
  );
}
